import React, { ChangeEvent, ClassAttributes, forwardRef, SelectHTMLAttributes } from "react";

import { bemClassNames, combineRefs, TAB_INDEX_FOCUSABLE_ORDER_EXPLICIT, useJavaScriptChange } from "@multiweb/reactUtils";
import { ForwardRefType, OnJavaScriptChange, OptionalTabIndex } from "@multiweb/types/react";
import { FormComponentProps } from "../types";

const classNames = bemClassNames("select-provider-component");

type CustomSelectProviderProps = {
    /**
     * Callback volaný při změně hodnoty pole.
     * Událost `onChange` je reactí událost a nepodobá se defaultnímu javascriptovému chování události `onChange`.
     * Podobá se spíše javascriptové události `onInput`.
     */
    onChange?: (value?: string) => void;
    /** Callback volaný při odposlechnutí javascriptové `change` eventy. */
    onJavaScriptChange?: OnJavaScriptChange;
    /** Umožňuje nastavit tabindex */
    tabIndex?: OptionalTabIndex | typeof TAB_INDEX_FOCUSABLE_ORDER_EXPLICIT;
    /** Přednastavená hodnota selectu */
    value?: string | ReadonlyArray<string> | number;
    /** Příznak, zda je jako hodnota selectu nastavena prázdná hodnota */
    isEmpty?: boolean;
}
& Pick<FormComponentProps, "readOnly">;

export type SelectHTMLProps = SelectHTMLAttributes<HTMLSelectElement> & ClassAttributes<HTMLSelectElement>;

export type SelectProviderProps = Omit<SelectHTMLProps, keyof CustomSelectProviderProps | "ref"> & CustomSelectProviderProps;

export const SelectProvider: ForwardRefType<HTMLSelectElement, SelectProviderProps> = forwardRef<HTMLSelectElement, SelectProviderProps>(({
    onJavaScriptChange,
    onChange,
    onBlur,
    children,
    disabled,
    readOnly,
    isEmpty,
    ...restProps
}, forwardRef) => {
    const refCallback = useJavaScriptChange(onJavaScriptChange);
    const ref = combineRefs(refCallback, forwardRef);

    const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
        if (onChange) {
            onChange(e.target.value);
        }
    };

    return (
        <div className={classNames({ isEmpty, readOnly, disabled })}>
            <select
                onChange={handleChange}
                {...{ disabled, onBlur, ref }}
                {...restProps}>
                {children}
            </select>
        </div>
    );
});
